/**
 * site-fixes.js
 * Small, targeted fixes layered on top of router.js and home-timeline.js.
 * Loaded (deferred) right after router.js in layout/theme.liquid.
 *
 * 1. Page transitions: run each external script file only once per visit.
 *    router.js re-inserted section scripts (header.js, home-timeline.js,
 *    blog-slider.js...) on every click because its "already loaded" check
 *    compared an absolute URL with the protocol-relative src attribute.
 *    That threw "Identifier 'Header' has already been declared" each time.
 *
 * 2. Homepage timeline sizing: the desktop carousel height is
 *    window height minus header, footer, year bar, labels and month row.
 *    On first load it could read --header-height / --footer-height before
 *    header.js and footer.js had set them (both 0), making the photos too
 *    tall so they ran under the footer. Now the real header and footer are
 *    measured right before every calculation, and it re-measures once fonts
 *    and the page have finished loading.
 *
 * 3. Safari carousel layout (home timeline + Lore / blog slider): card
 *    widths came only from height + aspect-ratio inside width:auto Swiper
 *    slides. Safari doesn't resolve that reliably, so Swiper measured the
 *    wrong slide widths and photos landed in the wrong place with big
 *    empty gaps. Cards now get an explicit width (height x ratio), and
 *    Swiper re-measures whenever a photo or video finishes loading.
 *
 * 4. Product page: the product's note (sticky note metafield) is moved
 *    under Details / Sizing / Shipping as a "letter from Brynn McIntosh"
 *    row that opens the note (styles in site-fixes.css).
 *
 * 5. Size diagram: a "See Diagram" link above the size chart on the
 *    products listed in SIZE_DIAGRAMS. It opens the diagram picture for
 *    the unit selected in the chart (IN or CM).
 */
(function () {
  /* ── 1. Router: one run per script file ── */
  if (typeof Router !== 'undefined' && !Router.prototype.__scriptGuard) {
    const normalize = (src) => {
      try {
        const u = new URL(src, window.location.href);
        return u.origin + u.pathname;
      } catch (e) {
        return src;
      }
    };

    const loaded = new Set();
    document.querySelectorAll('script[src]').forEach((s) => {
      loaded.add(normalize(s.getAttribute('src')));
    });

    Router.prototype.runScripts = function (container) {
      if (!container) return;
      container.querySelectorAll('script').forEach((oldScript) => {
        const src = oldScript.getAttribute('src');
        if (src) {
          const key = normalize(src);
          if (loaded.has(key)) return; // already ran on an earlier page
          loaded.add(key);
        }
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    };

    Router.prototype.__scriptGuard = true;
  }

  /* ── 3a. Safari: explicit card widths (CSS) ── */
  if (!document.getElementById('TimelineCardWidthFix')) {
    const style = document.createElement('style');
    style.id = 'TimelineCardWidthFix';
    style.textContent = [
      /* Home timeline cards */
      '.timeline-carousel .media-card { --card-h: var(--card-height, 500px); flex-shrink: 0; }',
      '.home-timeline__mobile-timeline .timeline-carousel .media-card { --card-h: 62vw; }',
      '.timeline-carousel .media-card--portrait { width: calc(var(--card-h) * 3 / 4); }',
      '.timeline-carousel .media-card--landscape { width: calc(var(--card-h) * 4 / 3); }',
      '.timeline-carousel .media-card--landscape-video { width: calc(var(--card-h) * 16 / 9); }',
      '.timeline-carousel .media-card--square { width: var(--card-h); }',
      /* Lore / blog slider cards (desktop only; mobile is one full width slide) */
      '@media screen and (min-width: 750px) {',
      '  .blog-slider .article-card-slider { width: calc(var(--card-height, 500px) * 2 / 3); }',
      '  .blog-slider .article-card-slider__image-wrapper { width: 100%; min-height: 0; aspect-ratio: auto !important; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  /* ── 3b. Re-measure Swiper when carousel media loads ── */
  const CAROUSELS = '.timeline-carousel, .blog-slider';
  const updateCarousel = (el) => {
    const carousel = el && el.closest ? el.closest(CAROUSELS) : null;
    if (carousel && carousel.swiper && !carousel.swiper.destroyed) carousel.swiper.update();
  };
  const updateAllCarousels = () => {
    document.querySelectorAll(CAROUSELS).forEach((c) => {
      if (c.swiper && !c.swiper.destroyed) c.swiper.update();
    });
  };
  if (!window.__timelineMediaListener) {
    window.__timelineMediaListener = true;
    // load / loadedmetadata don't bubble, so listen in the capture phase
    document.addEventListener('load', (e) => updateCarousel(e.target), true);
    document.addEventListener('loadedmetadata', (e) => updateCarousel(e.target), true);
    window.addEventListener('load', updateAllCarousels, { once: true });
    window.addEventListener('page:loaded', () => requestAnimationFrame(updateAllCarousels));
  }

  /* ── 4. Product page: the product's note becomes a message row under Shipping ── */
  const ENVELOPE_SVG =
    '<svg class="pm-message__icon" viewBox="0 0 24 18" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">' +
    '<rect x="0.5" y="0.5" width="23" height="17"/><path d="M0.5 0.5 12 10 23.5 0.5"/></svg>';
  const setupProductMessage = () => {
    document.querySelectorAll('.product-section').forEach((section) => {
      if (section.querySelector('.pm-message')) return;
      const timeline = section.querySelector('.product__timeline');
      const notes = Array.from(section.querySelectorAll('.product__gallery-blocks'));
      const note = notes.find((n) => n.textContent.trim());
      if (!timeline || !note) return;

      const wrap = document.createElement('div');
      wrap.className = 'pm-message';
      const bodyId = 'pm-message-' + (section.id || Math.random().toString(36).slice(2));
      wrap.innerHTML =
        '<button type="button" class="pm-message__toggle" aria-expanded="false" aria-controls="' + bodyId + '">' +
        ENVELOPE_SVG +
        '<span class="pm-message__dot" aria-hidden="true"></span>' +
        '<span class="pm-message__label">letter from Brynn McIntosh</span>' +
        '</button>' +
        '<div class="pm-message__body" id="' + bodyId + '"><div></div></div>';

      const copy = note.cloneNode(true);
      copy.className = 'product__gallery-blocks';
      wrap.querySelector('.pm-message__body > div').appendChild(copy);
      notes.forEach((n) => n.classList.add('pm-moved-away'));
      timeline.insertAdjacentElement('afterend', wrap);

      const toggle = wrap.querySelector('.pm-message__toggle');
      toggle.addEventListener('click', () => {
        const open = !wrap.classList.contains('is-open');
        wrap.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        if (window.lenis) setTimeout(() => window.lenis.resize(), 400);
      });
    });
  };
  if (!window.__productMessage) {
    window.__productMessage = true;
    setupProductMessage();
    document.addEventListener('DOMContentLoaded', setupProductMessage);
    window.addEventListener('page:loaded', () => requestAnimationFrame(setupProductMessage));
  }

  /* ── 5. Size diagram: "See Diagram" link above the size chart ── */
  /* Products with a diagram, by product handle. The picture that opens
     matches the unit selected in the chart (IN or CM). */
  const PATHFINDER_DIAGRAM = {
    in: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Mint-accented_hoodie_inches_chart-1.png?v=1793253967',
    cm: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Mint-accented_hoodie_measurement_chart-2.png?v=1793253968',
  };
  /* the standard ON/1025 hoodie chart (S to XXL, chest / body / sleeve) */
  const HOODIE_DIAGRAM = {
    in: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/ON.1025_hoodie_measurement_chart_IN.png?v=1793254914',
    cm: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Mint_hoodie_size_chart_in_centimeters.png?v=1793254879',
  };
  const VARSITY_DIAGRAM = {
    in: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Varsity_jacket_inch_measurement_chart_60f92caa-3ba3-4880-b1f4-bcb102563783.png?v=1793256214',
    cm: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Varsity_Jacket_cm_measurement.png?v=1793256199',
  };
  const LEATHER_JACKET_DIAGRAM = {
    in: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Jakcet_inch_measurement_chart.png?v=1793256252',
    cm: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Jacket_cm_measurement_size_chart.png?v=1793256268',
  };
  /* the standard ON/1025 t-shirt chart (S to XXL, chest / body / sleeve) */
  const TEE_DIAGRAM = {
    in: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Inked_T-shirt_measurement_size_chart-1.png?v=1793322952',
    cm: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/T-shirt_measurement_chart_in_centimeters-2.png?v=1793322963',
  };
  /* the standard ON/1025 sweatpants chart, cuffed jogger drawing (S to XXL, waist / inseam / front rise) */
  const SWEATPANTS_DIAGRAM = {
    in: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Cuffed_jogger_sweatpants_sizing_diagram-1.png?v=1793326542',
    cm: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Cuffed_sweatpants_centimeter_size_chart-2.png?v=1793326541',
  };
  /* "Introverted" Cargo Pants chart (S to XXL, waist / inseam / leg opening) */
  const CARGO_DIAGRAM = {
    in: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Cargo_pants_measurement_chart.png?v=1793582335',
    cm: 'https://cdn.shopify.com/s/files/1/0525/5236/8324/files/Cargo_pants_size_chart_in_centimeters.png?v=1793582336',
  };
  const SIZE_DIAGRAMS = {
    '1025-camo-cargo-pants': CARGO_DIAGRAM,                            // "Introverted" Cargo Pants
    'spider-cross-sweatpants': SWEATPANTS_DIAGRAM,                      // Spider Cross Sweatpants
    'into-the-forest-sweatpants-midnight': SWEATPANTS_DIAGRAM,          // Black Forest Sweatpants
    'copy-of-into-the-forest-sweatpants-charcoal': SWEATPANTS_DIAGRAM,  // Charcoal Forest Sweatpants
    'fear-of-8-t-shirt': TEE_DIAGRAM,                    // Fear of 8 T-Shirt
    'eights-web-t-shirt': TEE_DIAGRAM,                   // Eight's Web T-shirt
    'copy-of-eights-web-t-shirt-midnight': TEE_DIAGRAM,  // Midnight Hilltop T-shirt
    'monarch-8-t-shirt': TEE_DIAGRAM,                    // Monarch T-Shirt
    'protection-t-shirt-black-cream': TEE_DIAGRAM,       // Black & Cream Protection T-Shirt
    'protection-t-shirt-black-gray': TEE_DIAGRAM,        // Black & Gray Protection T-Shirt
    'gray-hilltop-t-shirt': TEE_DIAGRAM,                 // Gray Hilltop T-shirt
    'arthurs-quest-varsity-jacket': VARSITY_DIAGRAM,   // Arthur's Quest Varsity Jacket
    '10-25-leather-jacket': LEATHER_JACKET_DIAGRAM,    // 10/25 Leather Jacket
    'burnt-russet-pathfinder-hoodie': PATHFINDER_DIAGRAM,
    'dancing-flames-hoodie-smoke': HOODIE_DIAGRAM,   // Dancing Flames Hoodie - Phantom
    'over-the-moon-hoodie': HOODIE_DIAGRAM,          // Over the Moon Hoodie
    'fear-of-life-hoodie': HOODIE_DIAGRAM,           // Infinite Life Hoodie
    'monarch-8-hoodie': HOODIE_DIAGRAM,              // Monarch Hoodie
    'silent-fright-hoodie': HOODIE_DIAGRAM,          // Silent Fright Hoodie
    'spider-cross-hoodie': HOODIE_DIAGRAM,           // Spider Cross Hoodie
    'eights-charm-hoodie': HOODIE_DIAGRAM,           // Eight's Protection Hoodie
  };
  const currentHandle = () => {
    const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  };
  let diagramOverlay = null;
  const closeDiagram = () => {
    if (!diagramOverlay || diagramOverlay.hidden) return;
    diagramOverlay.hidden = true;
    document.documentElement.classList.remove('sd-open');
    if (window.lenis) window.lenis.start();
  };
  const openDiagram = (urls, unit) => {
    if (!diagramOverlay) {
      diagramOverlay = document.createElement('div');
      diagramOverlay.className = 'sd-overlay';
      diagramOverlay.hidden = true;
      diagramOverlay.setAttribute('role', 'dialog');
      diagramOverlay.setAttribute('aria-modal', 'true');
      diagramOverlay.setAttribute('aria-label', 'Size diagram');
      diagramOverlay.innerHTML =
        '<div class="sd-overlay__bar">' +
        '<span class="sd-overlay__units">' +
        '<button type="button" class="sd-unit" data-unit="in">IN</button>' +
        '<span aria-hidden="true">/</span>' +
        '<button type="button" class="sd-unit" data-unit="cm">CM</button>' +
        '</span>' +
        '<button type="button" class="sd-overlay__close" aria-label="Close diagram">Close</button>' +
        '</div>' +
        '<img class="sd-overlay__img" alt="Size diagram">';
      document.body.appendChild(diagramOverlay);
      diagramOverlay.addEventListener('click', (e) => {
        const unitBtn = e.target.closest('.sd-unit');
        if (unitBtn) {
          showDiagramUnit(unitBtn.dataset.unit);
          return;
        }
        if (e.target.closest('.sd-overlay__close') || e.target === diagramOverlay) closeDiagram();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeDiagram();
      });
    }
    diagramOverlay._urls = urls;
    showDiagramUnit(unit);
    diagramOverlay.hidden = false;
    document.documentElement.classList.add('sd-open');
    if (window.lenis) window.lenis.stop();
    diagramOverlay.querySelector('.sd-overlay__close').focus({ preventScroll: true });
  };
  const showDiagramUnit = (unit) => {
    const urls = diagramOverlay._urls;
    const u = urls[unit] ? unit : 'in';
    const img = diagramOverlay.querySelector('.sd-overlay__img');
    img.src = urls[u];
    img.alt = 'Size diagram (' + (u === 'cm' ? 'centimeters' : 'inches') + ')';
    diagramOverlay.querySelectorAll('.sd-unit').forEach((b) => {
      b.classList.toggle('is-active', b.dataset.unit === u);
    });
  };
  const setupSizeDiagram = () => {
    const urls = SIZE_DIAGRAMS[currentHandle()];
    if (!urls) return;
    document.querySelectorAll('.product-section sizing-table').forEach((chart) => {
      if (chart.previousElementSibling && chart.previousElementSibling.classList.contains('sd-link')) return;
      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'sd-link';
      link.textContent = 'See Diagram';
      link.addEventListener('click', () => {
        const active = chart.querySelector('.unit-btn.is-active');
        openDiagram(urls, active ? active.dataset.unit : 'in');
      });
      chart.insertAdjacentElement('beforebegin', link);
      // load both pictures ahead of time so they open instantly
      Object.values(urls).forEach((src) => { const i = new Image(); i.src = src; });
    });
  };
  if (!window.__sizeDiagram) {
    window.__sizeDiagram = true;
    setupSizeDiagram();
    document.addEventListener('DOMContentLoaded', setupSizeDiagram);
    window.addEventListener('page:loaded', () => {
      closeDiagram();
      requestAnimationFrame(setupSizeDiagram);
    });
  }

  /* ── 2. Home timeline: measure header + footer before sizing ── */
  customElements.whenDefined('home-timeline').then(() => {
    const HomeTimelineEl = customElements.get('home-timeline');
    if (!HomeTimelineEl || HomeTimelineEl.prototype.__metricsFix) return;

    const originalUpdate = HomeTimelineEl.prototype.updateUIMetrics;

    HomeTimelineEl.prototype.updateUIMetrics = function () {
      if (!this.isMobile && this.isConnected) {
        const root = document.documentElement;
        const header = document.querySelector('header-root .header-container');
        const footer = document.querySelector('footer-root');
        // Same values header.js / footer.js write, just guaranteed to be current
        if (header) root.style.setProperty('--header-height', `${header.offsetHeight}px`);
        if (footer && window.innerWidth >= 750) root.style.setProperty('--footer-height', `${footer.offsetHeight}px`);
      }
      return originalUpdate.call(this);
    };

    HomeTimelineEl.prototype.__metricsFix = true;

    const refresh = () => {
      document.querySelectorAll('home-timeline').forEach((el) => {
        if (typeof el.updateUIMetrics === 'function') el.updateUIMetrics();
      });
      updateAllCarousels(); // also covers the mobile timeline carousels
    };

    refresh();
    requestAnimationFrame(refresh);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', refresh, { once: true });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refresh);
    }
    // After an in-site page transition back to the homepage
    window.addEventListener('page:loaded', () => requestAnimationFrame(refresh));
  });
})();
